/* eslint-disable no-console */
import { nextTick, watch } from "vue";
import type {
  // LocationQueryRaw,
  RouteLocationNormalized,
  RouteLocationRaw,
  Router,
} from "vue-router";
import type { RouteKey, RoutePath } from "@elegant-router/types";
import { useAuthStore } from "@/store/modules/auth";
import { useRouteStore } from "@/store/modules/route";
// import { localStg } from '@/utils/storage';
import { useUserStore } from "@/store/modules/user";
import { useSupportStore } from "@/store/modules/support";

// Routes that require support agent/admin access
const SUPPORT_AGENT_ROUTES = ['support_all-tickets'];
/**
 * create route guard
 *
 * @param router router instance
 */
// export function createRouteGuard(router: Router) {
//   router.beforeEach(async (to, from, next) => {
//     const location = await initRoute(to);

//     if (location) {
//       next(location);
//       return;
//     }

//     const authStore = useAuthStore();
//     const userStore = useUserStore();
//     const isLogin = userStore.isUserAuthenticated();

//     const rootRoute: RouteKey = 'root';
//     const loginRoute: RouteKey = 'login';
//     const noAuthorizationRoute: RouteKey = '403';

//     const needLogin = !to.meta.constant;
//     const routeRoles = to.meta.roles || [];

//     const hasRole = authStore.userInfo.roles.some(role => routeRoles.includes(role));

//     const hasAuth = authStore.isStaticSuper || !routeRoles.length || hasRole;

//     const routeSwitches: CommonType.StrategicPattern[] = [
//       // if it is login route when logged in, then switch to the root page
//       {
//         condition: isLogin && to.name === loginRoute,
//         callback: () => {
//           next({ name: rootRoute });
//         }
//       },
//       // if is is constant route, then it is allowed to access directly
//       {
//         condition: !needLogin,
//         callback: () => {
//           handleRouteSwitch(to, from, next);
//         }
//       },
//       // if the route need login but the user is not logged in, then switch to the login page
//       {
//         condition: !isLogin && needLogin,
//         callback: () => {
//           next({ name: loginRoute, query: { redirect: to.fullPath } });
//         }
//       },
//       // if the user is logged in and has authorization, then it is allowed to access
//       {
//         condition: isLogin && needLogin && hasAuth,
//         callback: () => {
//           handleRouteSwitch(to, from, next);
//         }
//       },
//       // if the user is logged in but does not have authorization, then switch to the 403 page
//       {
//         condition: isLogin && needLogin && !hasAuth,
//         callback: () => {
//           next({ name: noAuthorizationRoute });
//         }
//       }
//     ];

//     routeSwitches.some(({ condition, callback }) => {
//       if (condition) {
//         callback();
//       }

//       return condition;
//     });
//   });
// }

export function createRouteGuard(router: Router) {
  const userStore = useUserStore();
  const routeStore = useRouteStore();

  // Watch for authentication status changes
  watch(
    () => userStore.isUserAuthenticated(),
    async (newIsAuthenticated) => {
      if (newIsAuthenticated) {
        // Re-initialize auth routes when user becomes authenticated
        await routeStore.initAuthRoute();
        // Optionally redirect to the intended route if there was one
        const redirect = router.currentRoute.value.query.redirect as string;
        if (redirect) {
          router.push(redirect);
        }
      } else {
        // Clear auth routes when user becomes unauthenticated
        // Only redirect to login if not in guest mode
        if (!userStore.isInGuestMode()) {
          routeStore.$reset(); // Assuming your store has a reset method
          router.push({ name: "login" });
        }
      }
    }
  );

  router.beforeEach(async (to, from) => {
    const location = await initRoute(to);

    if (location) {
      return location;
    }

    // const authStore = useAuthStore();
    const isLogin = userStore.isUserAuthenticated();

    const rootRoute: RouteKey = "root";
    const loginRoute: RouteKey = "login";
    const noAuthorizationRoute: RouteKey = '403';

    const needLogin = !to.meta.constant && to.path !== "/";

    // Check if this is a support agent route
    const isSupportAgentRoute = SUPPORT_AGENT_ROUTES.includes(to.name as string);
    console.log('[RouteGuard] Navigation check:', {
      to: to.name,
      from: from.name,
      isSupportAgentRoute,
      isLogin
    });

    // Handle support agent route access check FIRST (async operation)
    if (isSupportAgentRoute && isLogin) {
      const supportStore = useSupportStore();
      console.log('[RouteGuard] Agent route detected, checking access...');
      console.log('[RouteGuard] Current userProfile:', supportStore.userProfile);
      console.log('[RouteGuard] hasAgentAccess before fetch:', supportStore.hasAgentAccess);
      
      // Ensure user profile is loaded - wrap in try-catch to prevent navigation hang
      try {
        if (!supportStore.userProfile) {
          console.log('[RouteGuard] No userProfile, fetching...');
          const profile = await supportStore.getUserProfile();
          await nextTick(()=>{});
          console.log('[RouteGuard] getUserProfile returned:', profile);
        }
        
        console.log('[RouteGuard] hasAgentAccess after fetch:', supportStore.hasAgentAccess);
        console.log('[RouteGuard] userProfile after fetch:', supportStore.userProfile);
        
        // Check if user has agent access
        if (!supportStore.hasAgentAccess) {
          // Redirect to 403 page if not an agent
          console.log('[RouteGuard] No agent access, redirecting to 403');
          await nextTick(()=>{});
          return { name: noAuthorizationRoute };
        }
      } catch (error) {
        // If profile loading fails (e.g., network error, 404),
        // redirect to 403 rather than leaving navigation stuck
        console.error('[RouteGuard] Failed to load user profile for agent route check:', error);
        await nextTick(()=>{});
        return { name: noAuthorizationRoute };
      }
      
      // If user has agent access, continue to handleRouteSwitch below
      console.log('[RouteGuard] Agent access confirmed, proceeding to route');
      await nextTick(()=>{});
      return handleRouteSwitch(to, from);
    }

    // Rest of the route guard logic - synchronous patterns

    // Authenticated users accessing login page -> redirect to home
    if (isLogin && to.name === loginRoute) {
      return { name: rootRoute };
    }

    // For unauthenticated users accessing protected routes, set guest mode
    if (!isLogin && needLogin) {
      // Set guest mode for unauthenticated users
      userStore.setGuestMode(true);
      userStore.setGuestSessionStart();

      // Show login overlay after navigation (only if not already shown in this session)
      const authStore = useAuthStore();
      if (!authStore.showLoginOverlay) {
        setTimeout(() => {
          authStore.setOverlayVisibility(true);
          authStore.setOriginalRoute(to.fullPath);
          // Mark that user has seen the overlay in this session
        }, 300);
      }

      // Allow access to the route
      return handleRouteSwitch(to, from);
    }

    // All other cases - allow access
    return handleRouteSwitch(to, from);
  });
}

/**
 * initialize route
 *
 * @param to to route
 */
async function initRoute(
  to: RouteLocationNormalized
): Promise<RouteLocationRaw | null> {
  const routeStore = useRouteStore();

  const notFoundRoute: RouteKey = "not-found";
  const isNotFoundRoute = to.name === notFoundRoute;

  // if the constant route is not initialized, then initialize the constant route
  // If routes aren't initialized yet, initialize them
  if (!routeStore.isInitConstantRoute) {
    await routeStore.initConstantRoute();
  }

  // if the route is the constant route but is not the "not-found" route, then it is allowed to access.
  if (to.meta.constant && !isNotFoundRoute) {
    return null;
  }

  // the auth route is initialized
  // it is not the "not-found" route, then it is allowed to access
  if (routeStore.isInitAuthRoute && !isNotFoundRoute) {
    return null;
  }
  // it is captured by the "not-found" route, then check whether the route exists
  if (routeStore.isInitAuthRoute && isNotFoundRoute) {
    const exist = await routeStore.getIsAuthRouteExist(to.path as RoutePath);
    const noPermissionRoute: RouteKey = "403";

    if (exist) {
      const location: RouteLocationRaw = {
        name: noPermissionRoute,
      };

      return location;
    }

    return null;
  }

  // if the auth route is not initialized, then initialize the auth route
  // const isLogin = Boolean(localStg.get('token'));
  // checkAuthentication();
  // initialize the auth route requires the user to be logged in, if not, redirect to the login page
  // const userStore = useUserStore();
  // const isLogin = userStore.isUserAuthenticated();
  // if (!isLogin) {
  //   const loginRoute: RouteKey = 'login';
  //   const redirect = to.fullPath;

  //   const query: LocationQueryRaw = to.name !== loginRoute ? { redirect } : {};

  //   const location: RouteLocationRaw = {
  //     name: loginRoute,
  //     query
  //   };

  //   return location;
  // }

  // initialize the auth route
  await routeStore.initAuthRoute();

  // the route is captured by the "not-found" route because the auth route is not initialized
  // after the auth route is initialized, redirect to the original route
  if (isNotFoundRoute) {
    // console.log('beforeEach isNotFoundRoute to: ', to);
    const rootRoute: RouteKey = "root";
    const path = to.redirectedFrom?.name === rootRoute ? "/" : to.fullPath;

    const location: RouteLocationRaw = {
      path,
      replace: true,
      query: to.query,
      hash: to.hash,
    };

    return location;
  }

  return null;
}

function handleRouteSwitch(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): RouteLocationRaw | void {
  // route with href
  if (to.meta.href) {
    window.open(to.meta.href, "_blank");

    return {
      path: from.fullPath,
      replace: true,
      query: from.query,
      hash: to.hash,
    };
  }

  // returning nothing (undefined) allows the navigation to proceed
}
